import React, { useState, useEffect } from "react";
import { Row, Col, Button, FormGroup, Label, Input } from "reactstrap";
import { Formik } from "formik";
import axios from "axios";
import * as Yup from "yup";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { ItemRequisitionAPI } from "../ItemRequisitionAPI";
import FormStatus from "../../../components/FormStatus";
import { UserConsumer } from "../../../UserContext";
import "./CreateItemRequisitionLayout.scss";
import { AlertPopups } from "../../../components/AlertPopups";
import CustomReactSelect from "../../../components/CustomReactSelect";
import { Routes } from "../../../configurations/config";

function CreateItemRequisitionLayout() {
	let history = useHistory();
	const { id } = useParams();
	const { state } = useLocation();
	const itemStatus = state ? state.itemStatus : "Draft";
	let token = null;
	const initialValues = {
		projectName: "",
		itemName: "",
		reqPriority: "",
		description: "",
		justification: "",
	};
	const [isAddMode, setIsAddMode] = useState(true);
	const [formValues, setFormValues] = useState(initialValues);
	const [formStatus, setFormStatus] = useState(itemStatus);
	const [reqPriorityList, setReqPriorityList] = useState([]);
	const [apiHeaderToken, setApiHeaderToken] = useState(null);
	const validate = Yup.object().shape({
		projectName: Yup.string()
			.required("Enter Name")
			.typeError("Invalid input")
			.max(500, "Max 500 characters allowed"),
		itemName: Yup.string()
			.required("Description is required")
			.typeError("Invalid input")
			.max(500, "Max 500 characters allowed"),
		reqPriority: Yup.string()
			.required("Select Priority")
			.typeError("Invalid input")
			.nullable(),
		description: Yup.string()
			.required("Description is required")
			.typeError("Invalid input")
			.max(600, "Max 600 characters allowed"),
		justification: Yup.string()
			.required("Description is required")
			.typeError("Invalid input")
			.max(600, "Max 600 characters allowed"),
	});

	useEffect(() => {
		const tokenHeader = {
			Authorization: `Bearer ${token}`,
		};
		setApiHeaderToken(tokenHeader);
		if (id) {
			setIsAddMode(false);
			axios
				.get(`${ItemRequisitionAPI.GetItemRequisitionbyID}/${id}`, {
					headers: tokenHeader,
				})
				.then(({ data }) => {
					//setting value in dropdown
					const priority =
						data.priority === "Normal"
							? { value: 1, label: "Normal" }
							: { value: 1, label: "Urgent" };

					delete data["priority"];
					data["reqPriority"] = priority;
					//setting api response in from
					setFormValues(data);
				});
		}
		axios
			.get(`${ItemRequisitionAPI.GetReqPriorityList}`, {
				headers: tokenHeader,
			})
			.then(({ status, data }) => {
				if (status === 200) setReqPriorityList(data.priorities);
			});
	}, []);

	const handleChange = (temp, displayCode, e) => {
		if (displayCode === 3) {
			//dropdown
			setFormValues({ ...formValues, [e.name]: temp });
		} else {
			const { name, value } = temp.target;
			setFormValues({ ...formValues, [name]: value });
		}
	};
	const handleReset = () => {
		setFormValues(initialValues);
		setFormStatus("Draft");
	};
	const handleDelete = () => {
		AlertPopups.confirmBox
			.fire({
				text: "Delete Requested Item?",
			})
			.then((result) => {
				if (result.value) {
					return axios
						.delete(
							`${ItemRequisitionAPI.DeleteItemRequisitionbyId}/${id}`,
							{
								headers: apiHeaderToken,
							}
						)
						.then(({ status }) => {
							if (status === 200) {
								AlertPopups.successAlert.fire({
									text: "Requested Item Deleted",
								});
								history.push(Routes.ItemRequisition);
							} else {
								AlertPopups.ErrorAlert.fire({
									text: "Requested Item Deletion Error",
								});
							}
						});
				}
			});
	};

	const handleSubmit = () => {
		const formdata = {
			...formValues,
			["priority"]: formValues["reqPriority"].value,
		};
		if (isAddMode) {
			createItemRequisitionApplication(formdata);
		} else {
			updateItemRequisitionApplication(formdata);
		}
	};

	const createItemRequisitionApplication = (formdata) => {
		axios
			.post(`${ItemRequisitionAPI.PostItemRequisition}`, formdata, {
				headers: apiHeaderToken,
			})
			.then(({ status }) => {
				if (status === 200) {
					history.push(Routes.ItemRequisition);
					AlertPopups.successAlert.fire({
						text: "Item Requested",
					});
				} else {
					AlertPopups.ErrorAlert.fire({
						text: "Item Request Error",
					});
				}
			})
			.catch((err) => {
				if (err.response) {
					AlertPopups.ErrorAlert.fire({
						text: `${err.response.data.message}`,
					});
				} else
					AlertPopups.ErrorAlert.fire({
						text: "Item Request Error",
					});
			});
	};

	const updateItemRequisitionApplication = (formdata) => {
		axios
			.put(`${ItemRequisitionAPI.PutItemRequisition}/${id}`, formdata, {
				headers: apiHeaderToken,
			})
			.then(({ status }) => {
				if (status === 200) {
					setFormStatus("Updated");
					AlertPopups.successAlert.fire({
						text: "Requested Item Updated",
					});
					history.push(Routes.ItemRequisition);
				} else {
					AlertPopups.ErrorAlert.fire({
						text: "Requested Item Updation Error",
					});
				}
			})
			.catch((err) => {
				if (err.response) {
					AlertPopups.ErrorAlert.fire({
						text: `${err.response.data.message}`,
					});
				} else
					AlertPopups.ErrorAlert.fire({
						text: "Requested Item Updation Error",
					});
			});
	};

	return (
		<UserConsumer>
			{(userDetails) => {
				token = userDetails.token;
				return (
					<div className="container-fluid pl-3 pr-3 my-2">
						<Row className=" mb-3">
							<Col className="justify-content-between" lg={12}>
								<FormStatus
									id={id}
									formStatus={formStatus}
									className="create-request d-flex align-items-center"
									onClick={() => {
										history.push(Routes.ItemRequisition);
									}}
									navigateBack={true}
								/>
							</Col>
						</Row>

						<Formik
							enableReinitialize={true}
							initialValues={formValues}
							validationSchema={validate}
							validateOnChange={true}
							onSubmit={() => {
								handleSubmit();
							}}
						>
							{({ errors, handleSubmit }) => (
								<form
									onSubmit={handleSubmit}
									className="itemReqForm"
								>
									<FormGroup
										row
										id="row0"
										className="bottom-spacing topBorders"
									>
										<Label sm={12}>
											Item Requisition Form
										</Label>
									</FormGroup>
									<FormGroup
										row
										className="bottom-spacing rowStyle1"
									>
										<Label for="title" sm={2} md={3} lg={2}>
											Project/Department Name
											<span className="required">*</span>
										</Label>
										<Col sm={10} md={9} lg={10}>
											<Input
												type="text"
												name="projectName"
												id="projectName"
												className={`formValueInput ${
													errors.projectName
														? "h-auto invalid"
														: "h-auto"
												}`}
												onChange={(value, event) =>
													handleChange(
														value,
														1,
														event
													)
												}
												value={formValues.projectName}
												maxLength="500"
											/>
										</Col>
									</FormGroup>
									<FormGroup
										row
										className="bottom-spacing rowStyle2"
									>
										<Label for="title" sm={2} md={3} lg={2}>
											Item Name{" "}
											<span className="required">*</span>
										</Label>
										<Col sm={10} md={9} lg={10}>
											<Input
												type="text"
												name="itemName"
												id="itemName"
												className={`formValueInput ${
													errors.itemName
														? "h-auto invalid"
														: "h-auto"
												}`}
												onChange={(value, event) =>
													handleChange(
														value,
														1,
														event
													)
												}
												value={formValues.itemName}
												maxLength="500"
											/>
										</Col>
									</FormGroup>
									<FormGroup
										row
										className="bottom-spacing rowStyle1"
									>
										<Label
											for="selectPriority"
											sm={2}
											md={3}
											lg={2}
										>
											Req. Priority{" "}
											<span className="required">*</span>
										</Label>
										<Col sm={10} md={9} lg={10}>
											<CustomReactSelect
												name="reqPriority"
												id="reqPriority"
												onChange={(value, event) =>
													handleChange(
														value,
														3,
														event
													)
												}
												value={formValues.reqPriority}
												options={reqPriorityList}
												className={`formValueInput ${
													errors.reqPriority
														? "h-auto invalid"
														: "h-auto"
												}`}
											/>
										</Col>
									</FormGroup>

									<FormGroup
										row
										className="bottom-spacing rowStyle2"
									>
										<Label
											for="Description"
											sm={2}
											md={3}
											lg={2}
										>
											Description{" "}
											<span className="required">*</span>
										</Label>
										<Col sm={10} md={9} lg={10}>
											<Input
												type="textarea"
												name="description"
												id="description"
												onChange={(value, event) =>
													handleChange(
														value,
														1,
														event
													)
												}
												value={formValues.description}
												maxLength="600"
												className={`formValueInput ${
													errors.description
														? "h-auto invalid"
														: "h-auto"
												}`}
											/>
										</Col>
									</FormGroup>
									<FormGroup
										row
										className="bottom-spacing rowStyle1 lastRow"
									>
										<Label
											for="Description"
											sm={2}
											md={3}
											lg={2}
										>
											Please Justify Need{" "}
											<span className="required">*</span>
										</Label>
										<Col sm={10} md={9} lg={10}>
											<Input
												type="textarea"
												name="justification"
												id="justification"
												onChange={(value, event) =>
													handleChange(
														value,
														1,
														event
													)
												}
												value={formValues.justification}
												maxLength="600"
												className={`formValueInput ${
													errors.justification
														? "h-auto invalid"
														: "h-auto"
												}`}
											/>
										</Col>
									</FormGroup>
									<div className="btns">
										{id ? (
											itemStatus === "Pending" ? (
												<>
													<Button
														className="delete-btn leftBtn"
														onClick={handleDelete}
													>
														Delete
													</Button>
													<Button
														className="submit-btn leftBtn"
														onClick={handleSubmit}
													>
														Update
													</Button>
													<Button
														className="reset-btn"
														onClick={handleReset}
													>
														Reset
													</Button>
												</>
											) : null
										) : (
											<Button
												type="submit"
												className="submit-btn"
												onClick={handleSubmit}
											>
												Submit
											</Button>
										)}
									</div>
								</form>
							)}
						</Formik>
					</div>
				);
			}}
		</UserConsumer>
	);
}

export default CreateItemRequisitionLayout;
